// CurrentTripPage.jsx
// Landing Page: Current Trip Mode - Quick Daily Digests
// Slug: /current-trip

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPages.css';

// SEO Meta Tags
const SEO = {
  title: 'Capture Daily Travel Highlights | Current Trip Mode | VivaJour',
  description: 'Build quick 10-page digests of each day while you travel. Capture memories while fresh, share immediately with family, and never face photo backlog again. Works offline with on-device processing.',
  slug: '/current-trip'
};

export default function CurrentTripPage() {
  useEffect(() => {
    document.title = SEO.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = SEO.description;
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-nav-logo">
            <span>VivaJour</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/privacy-first" className="landing-nav-link">Privacy</Link>
            <Link to="/current-trip" className="landing-nav-link active">Current Trip</Link>
            <Link to="/albums" className="landing-nav-link">Albums</Link>
            <Link to="/how-it-works" className="landing-nav-link">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <span className="landing-hero-badge">📍 Share As You Go</span>
        <h1>Capture Today's Highlights<br />While They're Fresh</h1>
        <p className="landing-hero-subtitle">
          Just take photos normally while you travel. At day's end, open VivaJour, select your date(s), and it automatically includes all photos. Optionally refine with hearts/X, then get a beautiful digest (up to 10 pages) instantly. No background app, no manual selection required, zero friction.
        </p>
        <div className="landing-cta-group">
          <Link to="/how-it-works" className="landing-btn landing-btn-primary">
            See How It Works →
          </Link>
        </div>
        <div className="landing-trust-row">
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            No background app
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            Zero friction
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            Works offline
          </span>
        </div>
      </section>

      {/* The Problem */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2>Why Wait Until You Get Home?</h2>
          <p style={{ color: 'var(--landing-muted)', fontSize: '16px', lineHeight: '1.7', marginTop: '20px' }}>
            You take dozens of photos each day of your trip. You want to share highlights with family back home, but posting individual photos feels disorganized. Some apps make you manually select photos, tag favorites in real-time, or run battery-draining background services. By the time you get home, you can barely remember which day was which or why that photo mattered.
          </p>
          <p style={{ color: 'var(--landing-text)', fontSize: '17px', lineHeight: '1.7', marginTop: '20px', fontWeight: 600 }}>
            Current Trip mode solves this with maximum convenience: just select date(s) at day's end, and VivaJour automatically includes all photos. Optionally refine with hearts/X if you want. Get beautiful digest instantly. No manual selection required, no battery drain, no friction.
          </p>
        </div>
      </section>

      {/* How Current Trip Works */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>How Current Trip Mode Works</h2>
          <p className="landing-section-subtitle">
            Zero friction while traveling. Quick curation when you're ready.
          </p>
          
          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Take Photos Normally</h3>
                <p>Just use your camera app like always. No need to open VivaJour, no background app running, no tagging favorites in real-time. Enjoy your day without interruption.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Open VivaJour When Ready</h3>
                <p>At the end of the day (or whenever you have a moment), tap "Current Trip" and select your date(s). Optionally add a journal entry to capture your thoughts - VivaJour will create a dedicated journal page at the end of your digest.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>Review & Refine (Optional)</h3>
                <p>VivaJour intelligently selects diverse highlights from all photos in your selected dates. Want to refine? Heart your absolute favorites or X any you don't want. Or skip this entirely - your choice.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number">4</div>
              <div className="landing-step-content">
                <h3>Instant Digest Created</h3>
                <p>VivaJour creates a beautiful digest (up to 10 pages) from your photos. Share the link immediately or save for later.</p>
              <p className="landing-step-note">
  Place names are resolved using your device’s built-in geocoding services; VivaJour does not send location data to its own servers.
</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Why Build Daily Digests?</h2>
          
          <div className="landing-features-grid" style={{ marginTop: '40px' }}>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🔋</div>
              <h3>Zero Background Drain</h3>
              <p>No app running in the background draining your battery. Just take photos normally and curate later when you want.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🧠</div>
              <h3>Memories Are Fresh</h3>
              <p>Curate at end of day when you remember why that photo mattered, what you were feeling, who made you laugh. Context while it's vivid.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">⚡</div>
              <h3>Super Fast</h3>
              <p>Select date(s), app includes all photos automatically. Optionally refine in seconds. Get beautiful digest (up to 10 pages) instantly.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎯</div>
              <h3>No Real-Time Tagging</h3>
              <p>Don't interrupt your experience marking favorites. Just enjoy your day, then decide what mattered when you review.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📤</div>
              <h3>Share Mid-Trip</h3>
              <p>Send grandparents today's update tonight. Share organized highlights, not random camera roll dumps.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">✍️</div>
              <h3>Capture Your Story</h3>
              <p>Add journal entries when selecting your date - before even looking at photos. Record what made you laugh, what surprised you, how you felt. Gets a dedicated page at the end of your digest.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📁</div>
              <h3>Never Face Backlog</h3>
              <p>Each day becomes its own digest. Come home with your trip already organized by day, not facing a chaotic 500-photo backlog.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot/Demo Section */}
      <section className="landing-section">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>What a Daily Digest Looks Like</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 32px' }}>
            Up to 10 beautiful pages capturing today's best moments.
          </p>
          
          <div style={{ 
            background: 'var(--landing-card)', 
            border: '1px solid var(--landing-border)', 
            borderRadius: '16px', 
            padding: '32px', 
            textAlign: 'center' 
          }}>
            <div style={{ 
              background: 'rgba(14, 165, 233, 0.1)', 
              border: '2px dashed rgba(14, 165, 233, 0.3)', 
              borderRadius: '12px', 
              padding: '48px 24px',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '48px', margin: '0 0 12px' }}>📱</p>
              <p style={{ color: 'var(--landing-muted)', fontSize: '15px', margin: 0 }}>
                [Screenshot showing: Open app → Select date(s) → All photos automatically included → Optional heart/X refinement → Beautiful digest created instantly]
              </p>
            </div>
            <ul style={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0, 
              textAlign: 'left',
              fontSize: '15px',
              color: 'var(--landing-muted)'
            }}>
              <li style={{ padding: '8px 0', display: 'flex', gap: '10px' }}>
                <span style={{ color: '#0ea5e9' }}>✓</span>
                <span>No app needed while taking photos</span>
              </li>
              <li style={{ padding: '8px 0', display: 'flex', gap: '10px' }}>
                <span style={{ color: '#0ea5e9' }}>✓</span>
                <span>Select date(s) — all photos from those days automatically included</span>
              </li>
              <li style={{ padding: '8px 0', display: 'flex', gap: '10px' }}>
                <span style={{ color: '#0ea5e9' }}>✓</span>
                <span>Optionally heart favorites or X unwanted ones (skip if you want)</span>
              </li>
              <li style={{ padding: '8px 0', display: 'flex', gap: '10px' }}>
                <span style={{ color: '#0ea5e9' }}>✓</span>
                <span>Beautiful digest (up to 10 pages) created instantly</span>
              </li>
              <li style={{ padding: '8px 0', display: 'flex', gap: '10px' }}>
                <span style={{ color: '#0ea5e9' }}>✓</span>
                <span>Share link immediately or save for later</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Perfect For */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Perfect For</h2>
          
          <div className="landing-use-cases" style={{ marginTop: '40px' }}>
            <div className="landing-use-case">
              <p className="landing-use-case-title">Sharing With Family</p>
              <p className="landing-use-case-desc">Grandparents get daily updates they can actually view, not 47 unorganized photos.</p>
            </div>
            
            <div className="landing-use-case">
              <p className="landing-use-case-title">Memory While Fresh</p>
              <p className="landing-use-case-desc">Lock in the emotions and context of each day before they fade.</p>
            </div>
            
            <div className="landing-use-case">
              <p className="landing-use-case-title">Long Trips</p>
              <p className="landing-use-case-desc">2-week trips generate thousands of photos. Daily digests keep it manageable.</p>
            </div>
            
            <div className="landing-use-case">
              <p className="landing-use-case-title">Business + Leisure</p>
              <p className="landing-use-case-desc">Blend work travel with personal exploration. Consolidate each day's highlights.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Modes Better Together */}
      <section className="landing-section">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Current Trip + Past Trip: Better Together</h2>
          <p className="landing-section-subtitle">
            Two powerful modes for different moments in your travel journey
          </p>
          
          <div className="landing-modes-grid">
            <div className="landing-mode-card">
              <div className="landing-mode-badge" style={{ background: '#4A90E2', color: 'white', padding: '6px 16px', borderRadius: '20px', display: 'inline-block', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
                📍 While Traveling
              </div>
              <h3>Current Trip Mode</h3>
              <p className="landing-mode-description" style={{ color: 'var(--landing-muted)', marginBottom: '20px' }}>
                Quick daily digests to share as you go
              </p>
              <ul className="landing-mode-features" style={{ listStyle: 'none', padding: 0, marginBottom: '20px' }}>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ 2 minutes per day</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Share with family tonight</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Capture thoughts while fresh</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Never face photo backlog</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Up to 10 pages per day</li>
              </ul>
              <p className="landing-mode-note" style={{ fontSize: '14px', color: 'var(--landing-muted)', fontStyle: 'italic', borderTop: '1px solid var(--landing-border)', paddingTop: '16px' }}>
                Perfect for: Keeping loved ones updated, documenting each day's story, avoiding overwhelming photo backlogs
              </p>
            </div>
            
            <div className="landing-mode-card">
              <div className="landing-mode-badge" style={{ background: '#7B68EE', color: 'white', padding: '6px 16px', borderRadius: '20px', display: 'inline-block', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
                🏠 After Returning
              </div>
              <h3>Past Trip Mode</h3>
              <p className="landing-mode-description" style={{ color: 'var(--landing-muted)', marginBottom: '20px' }}>
                Comprehensive albums from your entire trip
              </p>
              <ul className="landing-mode-features" style={{ listStyle: 'none', padding: 0, marginBottom: '20px' }}>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Full trip storytelling</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ 20-100+ pages of memories</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Professional templates</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Complete narrative arc</li>
                <li style={{ padding: '8px 0', color: 'var(--landing-text)' }}>✓ Print-ready quality</li>
              </ul>
              <p className="landing-mode-note" style={{ fontSize: '14px', color: 'var(--landing-muted)', fontStyle: 'italic', borderTop: '1px solid var(--landing-border)', paddingTop: '16px' }}>
                Perfect for: Creating keepsake albums, comprehensive trip documentation, professional photo books
              </p>
            </div>
          </div>
          
          <div className="landing-callout" style={{ marginTop: '50px', background: 'var(--landing-surface)', padding: '30px', borderRadius: '12px', border: '2px solid var(--landing-border)', textAlign: 'center' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '20px' }}>Use Both for the Complete Experience</h4>
            <p style={{ color: 'var(--landing-muted)', lineHeight: '1.7', maxWidth: '700px', margin: '0 auto' }}>
              Create quick daily digests while traveling, then build your full album when you return. Your daily digests don't replace the album - they complement it by capturing real-time moments and keeping photo fatigue at bay.
            </p>
          </div>
          
          <p style={{ color: 'var(--landing-muted)', fontSize: '14px', marginTop: '30px', textAlign: 'center' }}>
            Both modes use the same on-device processing and privacy protections.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>Frequently Asked Questions</h2>
          
          <div className="landing-faq">
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Do I have to select photos manually?</h4>
              <p className="landing-faq-answer">No! Just select the date(s) and VivaJour automatically includes all photos from those days. You can optionally heart favorites or X unwanted ones before building, but it's completely optional. Most users just pick dates and go.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What if I take more than 10 pages worth of photos in a day?</h4>
              <p className="landing-faq-answer">The digest creates up to 10 pages. If you have many photos, VivaJour intelligently curates to the best shots. You can refine the selection by hearting absolute favorites before building.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I still create a full album later?</h4>
              <p className="landing-faq-answer">Absolutely. Your daily digests are quick highlights for immediate sharing. When you get home, use Past Trip mode to create a comprehensive album from all your photos.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What if I miss a day?</h4>
              <p className="landing-faq-answer">No problem. You can create digests for any past date during your trip. Just select the date range you want.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Do I need internet while traveling?</h4>
              <p className="landing-faq-answer">No. Photo selection and digest creation work completely offline. You only need internet when you want to share the digest link. <strong>Tip:</strong> Keep location services enabled on your camera - this allows your phone to embed GPS coordinates in your photos, which VivaJour uses to organize by location and add place names.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How long do share links last?</h4>
              <p className="landing-faq-answer">On the free tier, share links are active for a limited time. With Pro, your share links stay active until you delete the album. You maintain full control over your shared content.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I edit the digest before sharing?</h4>
              <p className="landing-faq-answer">Yes. You can add/remove photos, change templates, or add journaling before sharing.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I add journal entries to my digests?</h4>
              <p className="landing-faq-answer">Yes! When you select your date(s), you can add a journal entry that becomes a dedicated page at the end of your digest. It's a great way to capture your thoughts and feelings while the day is still fresh, before even reviewing your photos. if you forgot, no problem, you can still edit the album and add a journal template.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-callout">
            <h3>Start Capturing Your Trip Today</h3>
            <p>Try free for 30 days. Quick daily digests. Share as you go.</p>
            <div className="landing-cta-group">
              <a href="https://apps.apple.com/app/id6756980207" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on iOS
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.yourco.traveltracker" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on Android
              </a>
            </div>
          </div>
          
          {/* Internal Links */}
          <div className="landing-internal-links">
            <Link to="/albums" className="landing-internal-link">
              📚 Past Trip Albums — Create full albums from camera roll
            </Link>
            <Link to="/privacy-first" className="landing-internal-link">
              🔒 Privacy-First — How we protect your photos
            </Link>
            <Link to="/how-it-works" className="landing-internal-link">
              ⚙️ How It Works — See the full process
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/templates" className="landing-footer-link">Templates</Link>
        </div>
        <p className="landing-footer-meta">
          VivaJour Labs LLC • Built in Wisconsin
        </p>
        <p className="landing-footer-meta" style={{ marginTop: '8px' }}>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
          {' • '}
          <Link to="/terms" className="landing-footer-link">Terms of Service</Link>
          {' • '}
          <Link to="/contact" className="landing-footer-link">Contact</Link>
        </p>
        <p className="landing-footer-tagline">
          © 2026 VivaJour. Your memories stay on your device. Your digests stay yours.
        </p>
      </footer>
    </div>
  );
}